import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Check, ChevronRight } from 'lucide-react';
import './ContextMenu.css';

/**
 * Generic right-click context menu with the same look as the Collections
 * sidebar menu (fixed rounded panel + .ctx-menu-btn rows). Rendered in a
 * portal, clamped to the viewport, closed on outside mousedown or Escape.
 *
 * items: array of { icon, label, onClick, disabled, danger } — falsy entries
 * are skipped, so callers can build the list with conditionals inline.
 * `{ separator: true }` draws a thin rule between groups.
 * `{ icon, label, items: [...] }` is one row that opens a submenu ("Move to ›"
 * → the lists), built like the sidebar's "Add ›" / "Share ›" (`.ctx-submenu*`):
 * it opens on hover, focus or click, on whichever side it fits (SubmenuPanel
 * measures itself); picking a row in it runs it and closes the whole menu.
 * `danger` paints a destructive row red via the sidebar's existing
 * `ctx-menu-btn--danger`; without it a menu ending in Delete had to be
 * hand-rolled to keep that colour, which is how the schema sidebar ended up
 * with its own un-portaled copy that the content pane painted over
 * (owner, 22.09.2026).
 *
 * className: optional extra class(es) appended to the root panel, so a caller
 * can recognise its own menu from a document-level listener (`closest(...)`).
 * Empty by default — existing callers render byte-identical markup.
 *
 * checked: a row (or submenu row) with `checked: true | false` is one choice
 * of a set (Status, Priority). The picked one shows a check at the END of the
 * row, right-aligned like VS Code / Linear menus, instead of a "  ✓" glued to
 * its label (owner, 13.10.2026). Such rows are role="menuitemradio" with
 * aria-checked; rows without the flag keep their markup.
 *
 * preserveFocus: clicking a row does not take focus (or the text selection)
 * away from the element that was right-clicked. The app-wide text menu needs
 * this so Cut/Paste act on the field the user was in. Off by default.
 *
 * align: 'end' makes `x` the menu's RIGHT edge (a menu opened under a button at
 * the right of a header lines up with it). Opt-in; 'start' is the default.
 *
 * hint: a row (or submenu row) with `hint: 'F2'` shows that shortcut as a kbd
 * chip at the end of the row (the Documents row menu, 13.10.2026). Opt-in.
 *
 * keyboard: a menu-button menu (the board tile's ⋯, 13.10.2026) — the panel is
 * role="menu" named by `label`, its rows are role="menuitem", the first row
 * takes focus on open, ArrowUp / ArrowDown / Home / End move between rows, and
 * Escape or Tab closes it and hands focus back to whatever had it on open
 * (the ⋯ button). Opt-in, so every other caller keeps its markup.
 */
const EDGE = 8;

/**
 * A submenu's panel, placed by measuring itself once open (owner, 06.10.2026:
 * "shown on the opposite side when the window is too narrow"): to the right of
 * its row when it fits, else to the left; if neither fits, on the side with
 * more room. It also moves up rather than run past the bottom of the window.
 * Hidden for the one frame before it is measured, so it never flashes on the
 * wrong side.
 */
function SubmenuPanel({ label, children }) {
  const ref = useRef(null);
  const [place, setPlace] = useState(null); // { left: boolean, shift: number }
  useLayoutEffect(() => {
    const el = ref.current;
    const row = el?.parentElement?.getBoundingClientRect();
    if (!row) return;
    const w = el.offsetWidth;
    const h = el.offsetHeight;
    const roomRight = window.innerWidth - EDGE - (row.right - 4);
    const roomLeft = row.left + 4 - EDGE;
    const left = w > roomRight && (w <= roomLeft || roomLeft > roomRight);
    const top = row.top - 8;
    const shift = Math.max(EDGE - top, Math.min(0, window.innerHeight - EDGE - (top + h)));
    setPlace({ left, shift });
  }, []);
  return (
    <div
      ref={ref}
      role="menu"
      aria-label={label}
      style={{ minWidth: 180, whiteSpace: 'nowrap', visibility: place ? 'visible' : 'hidden', ...(place?.shift ? { top: -8 + place.shift } : {}) }}
      className={`ctx-submenu-panel rounded-lg border border-gray-200 bg-white py-2 shadow-lg dark:border-zinc-700 dark:bg-zinc-900${place?.left ? ' ctx-submenu-panel--left' : ''}`}
    >
      {children}
    </div>
  );
}

const ROWS = ':scope > [role="menuitem"], :scope > [role="menuitemradio"], :scope > .ctx-submenu > [role="menuitem"]';
const rowsOf = (root) => Array.from(root?.querySelectorAll(ROWS) || []).filter((el) => !el.disabled);

const isChoice = (item) => typeof item.checked === 'boolean';
const choiceProps = (item) => (isChoice(item) ? { role: 'menuitemradio', 'aria-checked': item.checked } : {});
const checkMark = (item) => (item.checked ? <Check size={14} className="ctx-menu-check" aria-hidden="true" /> : null);
const hintChip = (item) => (item.hint ? <kbd className="ctx-menu-hint" aria-hidden="true">{item.hint}</kbd> : null);

export default function ContextMenu({ x, y, items, onClose, zIndex, className = '', preserveFocus = false, align = 'start', keyboard = false, label }) {
  const ref = useRef(null);
  const openedAt = useRef(Date.now());
  // The element that had focus when the menu opened (keyboard mode only).
  const opener = useRef(keyboard && typeof document !== 'undefined' ? document.activeElement : null);
  const [pos, setPos] = useState({ left: x, top: y });
  const [openSub, setOpenSub] = useState(null);

  // Keep the menu fully on screen when opened near the right/bottom edges.
  useLayoutEffect(() => {
    const rect = ref.current?.getBoundingClientRect();
    if (!rect) return;
    const wantLeft = align === 'end' ? x - rect.width : x;
    setPos({
      left: Math.max(8, Math.min(wantLeft, window.innerWidth - rect.width - 8)),
      top: Math.max(8, Math.min(y, window.innerHeight - rect.height - 8)),
    });
  }, [x, y, align]);

  useEffect(() => {
    const onDown = (e) => {
      if (Date.now() - openedAt.current < 250) return;
      if (ref.current?.contains(e.target)) return;
      onClose();
    };
    const onKey = (e) => {
      if (e.key === 'Escape' || (keyboard && e.key === 'Tab')) {
        if (e.key === 'Tab') e.preventDefault();
        e.stopPropagation();
        onClose();
        if (keyboard) opener.current?.focus?.({ preventScroll: true });
      }
    };
    // Capture phase: a dialog that stops mousedown from bubbling (the task modal
    // does, so a click inside it never closes it) must not keep a menu open.
    document.addEventListener('mousedown', onDown, true);
    window.addEventListener('keydown', onKey, true);
    return () => {
      document.removeEventListener('mousedown', onDown, true);
      window.removeEventListener('keydown', onKey, true);
    };
  }, [onClose, keyboard]);

  useEffect(() => {
    if (keyboard) rowsOf(ref.current)[0]?.focus({ preventScroll: true });
  }, [keyboard]);

  const onMenuKeyDown = (e) => {
    const rows = rowsOf(ref.current);
    if (!rows.length) return;
    const at = rows.indexOf(document.activeElement);
    let next = null;
    if (e.key === 'ArrowDown') next = rows[(at + 1) % rows.length];
    else if (e.key === 'ArrowUp') next = rows[(at - 1 + rows.length) % rows.length];
    else if (e.key === 'Home') next = rows[0];
    else if (e.key === 'End') next = rows[rows.length - 1];
    if (!next) return;
    e.preventDefault();
    next.focus({ preventScroll: true });
  };
  const rowRole = keyboard ? 'menuitem' : undefined;
  const rowTab = keyboard ? -1 : undefined;

  return createPortal(
    <div
      ref={ref}
      className={`fixed z-50 min-w-40 animate-fadeIn rounded-lg border border-gray-200 bg-white py-2 shadow-lg dark:border-zinc-700 dark:bg-zinc-900${className ? ` ${className}` : ''}`}
      style={{ left: pos.left, top: pos.top, ...(zIndex ? { zIndex } : {}) }}
      onContextMenu={(e) => e.preventDefault()}
      onMouseDown={preserveFocus ? (e) => e.preventDefault() : undefined}
      {...(keyboard ? { role: 'menu', 'aria-label': label, onKeyDown: onMenuKeyDown } : {})}
    >
      {items.filter(Boolean).map((item, i) => {
        if (item.separator) return <div key={i} role="separator" className="ctx-menu-sep" />;
        if (item.items) {
          const subItems = item.items.filter(Boolean);
          const open = openSub === i;
          return (
            <div key={i} className="ctx-submenu" onMouseEnter={() => setOpenSub(i)} onMouseLeave={() => setOpenSub(null)}>
              <button
                type="button"
                className="ctx-menu-btn"
                role={rowRole}
                tabIndex={rowTab}
                aria-haspopup="menu"
                aria-expanded={open}
                disabled={item.disabled || !subItems.length}
                onClick={() => setOpenSub(open ? null : i)}
                onFocus={() => setOpenSub(i)}
              >
                {item.icon}
                <span>{item.label}</span>
                <ChevronRight size={13} className="ctx-submenu-caret" />
              </button>
              {open && (
                <SubmenuPanel label={item.label}>
                  {subItems.map((sub, j) => (
                    <button
                      key={j}
                      type="button"
                      role="menuitem"
                      {...choiceProps(sub)}
                      className="ctx-menu-btn"
                      disabled={sub.disabled}
                      onClick={() => {
                        sub.onClick?.();
                        onClose();
                      }}
                    >
                      {sub.icon}
                      <span>{sub.label}</span>
                      {checkMark(sub)}
                      {hintChip(sub)}
                    </button>
                  ))}
                </SubmenuPanel>
              )}
            </div>
          );
        }
        return (
          <button
            key={i}
            type="button"
            role={rowRole}
            {...choiceProps(item)}
            tabIndex={rowTab}
            className={`ctx-menu-btn${item.danger ? ' ctx-menu-btn--danger' : ''}`}
            disabled={item.disabled}
            onClick={() => {
              item.onClick?.();
              onClose();
            }}
          >
            {item.icon}
            <span>{item.label}</span>
            {checkMark(item)}
            {hintChip(item)}
          </button>
        );
      })}
    </div>,
    document.body
  );
}
